import { useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import {
  ApiResourceTable,
  type ApiResourceExtraColumn,
} from "@project/admin/components/api-resource-table";
import type { Field } from "@project/admin/components/resource-table";
import {
  franchiseEnquiryService,
  employmentInquiryService,
} from "@project/services/admin";
import type {
  FranchiseEnquiryPayload,
  FranchiseEnquiryRecord,
  EmploymentInquiryPayload,
  EmploymentInquiryRecord,
} from "@project/types/gruh-udhyog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Badge } from "@project/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@project/components/ui/tabs";
import {
  Phone,
  Mail,
  MapPin,
  Briefcase,
  MessageSquare,
  User,
  Building2,
  UserCheck,
} from "lucide-react";
import { cn } from "@project/lib/utils";

// ===========================================================================
// Franchise Enquiries Setup
// ===========================================================================
const franchiseFields: Field[] = [
  {
    name: "full_name",
    label: "Full Name",
    required: true,
    showInTable: true,
    placeholder: "e.g. Harshal Thakar",
  },
  {
    name: "phone_number",
    label: "Phone Number",
    type: "tel",
    required: true,
    showInTable: true,
    placeholder: "e.g. +91 9876543210",
  },
  {
    name: "email_address",
    label: "Email Address",
    type: "email",
    showInTable: true,
    placeholder: "e.g. contact@example.com",
  },
  {
    name: "city_location",
    label: "City / Location",
    required: true,
    showInTable: true,
    placeholder: "e.g. Shimla",
  },
  {
    name: "business_experience",
    label: "Business Experience",
    showInTable: false,
    placeholder: "e.g. 5 years in retail",
  },
  {
    name: "message",
    label: "Message",
    type: "textarea",
    showInTable: false,
    placeholder: "Any comments or queries...",
  },
  {
    name: "created_at",
    label: "Submitted On",
    type: "date",
    readOnly: true,
    showInTable: true,
  },
];

const franchiseExtraColumns: ApiResourceExtraColumn<FranchiseEnquiryRecord>[] = [
  {
    id: "city_badge",
    label: "City",
    headerClassName: "w-36",
    cellClassName: "w-36",
    render: row => (
      <Badge
        variant="outline"
        className="border-primary/30 bg-primary/5 text-primary font-medium"
      >
        <MapPin className="mr-1 h-3 w-3" />
        {row.city_location ?? "—"}
      </Badge>
    ),
  },
];

function toFranchisePayload(raw: Record<string, unknown>): FranchiseEnquiryPayload {
  return {
    full_name: String(raw.full_name ?? "").trim(),
    phone_number: String(raw.phone_number ?? "").trim(),
    email_address: raw.email_address
      ? String(raw.email_address).trim()
      : undefined,
    city_location: String(raw.city_location ?? "").trim(),
    business_experience: raw.business_experience
      ? String(raw.business_experience).trim()
      : undefined,
    message: raw.message ? String(raw.message).trim() : undefined,
  };
}

// ===========================================================================
// Employee Inquiries Setup
// ===========================================================================
const employmentFields: Field[] = [
  {
    name: "name",
    label: "Applicant Name",
    required: true,
    showInTable: true,
    placeholder: "e.g. Rahul Patel",
  },
  {
    name: "mobile_number",
    label: "Mobile Number",
    type: "tel",
    required: true,
    showInTable: true,
    placeholder: "e.g. 9876543210",
  },
  {
    name: "is_looking_for_employment",
    label: "Looking for Employment?",
    type: "select",
    options: [
      { value: "true", label: "Yes (Seeking Employment)" },
      { value: "false", label: "No (General Enquiry)" },
    ],
    required: true,
    showInTable: false,
  },
  {
    name: "additional_details",
    label: "Additional Details",
    type: "textarea",
    showInTable: false,
    placeholder: "Provide any additional details or background...",
  },
  {
    name: "created_at",
    label: "Submitted On",
    type: "date",
    readOnly: true,
    showInTable: true,
  },
];

const employmentExtraColumns: ApiResourceExtraColumn<EmploymentInquiryRecord>[] = [
  {
    id: "employment_status",
    label: "Seeking Job?",
    headerClassName: "w-44",
    cellClassName: "w-44",
    render: row => (
      <Badge
        variant="outline"
        className={cn(
          "font-medium",
          row.is_looking_for_employment
            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
            : "border-muted bg-muted/40 text-muted-foreground"
        )}
      >
        {row.is_looking_for_employment ? "Yes" : "No"}
      </Badge>
    ),
  },
];

function toEmploymentPayload(raw: Record<string, unknown>): EmploymentInquiryPayload {
  return {
    name: String(raw.name ?? "").trim(),
    mobile_number: String(raw.mobile_number ?? "").trim(),
    is_looking_for_employment:
      raw.is_looking_for_employment === true ||
      raw.is_looking_for_employment === "true",
    additional_details: raw.additional_details
      ? String(raw.additional_details).trim()
      : undefined,
  };
}

// ===========================================================================
// Detail Info Row Helper
// ===========================================================================
function InfoRow({
  icon: Icon,
  label,
  value,
  children,
}: {
  icon: React.ElementType;
  label: string;
  value?: string | null;
  children?: React.ReactNode;
}) {
  if (!value && !children) return null;
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        {children ? (
          <div className="mt-0.5">{children}</div>
        ) : (
          <p className="mt-0.5 text-sm font-semibold text-foreground break-words">
            {value}
          </p>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Main Page Component
// ===========================================================================
export default function InquiriesPage() {
  const [activeTab, setActiveTab] = useState<"franchise" | "employment">("franchise");
  const [selectedFranchise, setSelectedFranchise] = useState<FranchiseEnquiryRecord | null>(null);
  const [selectedEmployment, setSelectedEmployment] = useState<EmploymentInquiryRecord | null>(null);

  return (
    <AdminShell
      title="Inquiries"
      subtitle="Manage franchise interest requests and employee job inquiries"
    >
      <Tabs
        value={activeTab}
        onValueChange={v => setActiveTab(v as "franchise" | "employment")}
        variant="line"
        className="space-y-6"
      >
        <TabsList>
          <TabsTrigger value="franchise" className="shrink-0 gap-2">
            <Building2 className="h-4 w-4" />
            <span>Franchise Enquiries</span>
          </TabsTrigger>
          <TabsTrigger value="employment" className="shrink-0 gap-2">
            <UserCheck className="h-4 w-4" />
            <span>Employee Inquiries</span>
          </TabsTrigger>
        </TabsList>

        {/* ------------------------------------------------------------------ */}
        {/* Tab 1: Franchise Enquiries                                          */}
        {/* ------------------------------------------------------------------ */}
        <TabsContent value="franchise" className="mt-0 focus-visible:outline-none">
          <ApiResourceTable<FranchiseEnquiryRecord>
            title="Franchise Enquiry"
            labelField="full_name"
            fields={franchiseFields}
            extraColumns={franchiseExtraColumns}
            searchFields={["full_name", "phone_number", "email_address", "city_location"]}
            defaultValues={{}}
            enableCsvExport
            csvFileName="franchise-enquiries"
            list={query => franchiseEnquiryService.listPage(query)}
            loadRecordForEdit={id => franchiseEnquiryService.get(id)}
            create={payload => franchiseEnquiryService.create(toFranchisePayload(payload))}
            update={(id, payload) =>
              franchiseEnquiryService.update(id, toFranchisePayload(payload))
            }
            remove={id => franchiseEnquiryService.delete(id)}
            onView={row => setSelectedFranchise(row)}
          />
        </TabsContent>

        {/* ------------------------------------------------------------------ */}
        {/* Tab 2: Employee Inquiries                                           */}
        {/* ------------------------------------------------------------------ */}
        <TabsContent value="employment" className="mt-0 focus-visible:outline-none">
          <ApiResourceTable<EmploymentInquiryRecord>
            title="Employee Inquiry"
            addButtonLabel="Add Employee Inquiry"
            labelField="name"
            fields={employmentFields}
            extraColumns={employmentExtraColumns}
            searchFields={["name", "mobile_number", "additional_details"]}
            defaultValues={{ is_looking_for_employment: "true" }}
            mapRecordToForm={record => ({
              ...record,
              is_looking_for_employment: String(record.is_looking_for_employment ?? true),
            })}
            enableCsvExport
            csvFileName="employee-inquiries"
            list={query => employmentInquiryService.listPage(query)}
            loadRecordForEdit={id => employmentInquiryService.get(id)}
            create={payload => employmentInquiryService.create(toEmploymentPayload(payload))}
            update={(id, payload) =>
              employmentInquiryService.update(id, toEmploymentPayload(payload))
            }
            remove={id => employmentInquiryService.delete(id)}
            onView={row => setSelectedEmployment(row)}
          />
        </TabsContent>
      </Tabs>

      {/* ------------------------------------------------------------------ */}
      {/* Franchise Enquiry Detail Dialog                                    */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={selectedFranchise !== null}
        onOpenChange={open => {
          if (!open) setSelectedFranchise(null);
        }}
      >
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-bold">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                <Building2 className="h-4 w-4 text-primary" />
              </span>
              Franchise Enquiry Details
            </DialogTitle>
          </DialogHeader>

          {selectedFranchise && (
            <div className="space-y-4 text-sm">
              <div className="rounded-xl border border-border bg-muted/30 px-4 py-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  Applicant
                </p>
                <p className="mt-1 text-xl font-bold text-foreground">
                  {selectedFranchise.full_name}
                </p>
                {selectedFranchise.created_at && (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Submitted on{" "}
                    {new Date(selectedFranchise.created_at).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <InfoRow
                  icon={Phone}
                  label="Phone Number"
                  value={selectedFranchise.phone_number}
                />
                <InfoRow
                  icon={Mail}
                  label="Email Address"
                  value={selectedFranchise.email_address}
                />
                <InfoRow
                  icon={MapPin}
                  label="City / Location"
                  value={selectedFranchise.city_location}
                />
                <InfoRow
                  icon={Briefcase}
                  label="Business Experience"
                  value={selectedFranchise.business_experience}
                />
              </div>

              {selectedFranchise.message && (
                <div className="rounded-xl border border-border bg-muted/20 p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-primary" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Message
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap">
                    {selectedFranchise.message}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Employee Inquiry Detail Dialog                                      */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={selectedEmployment !== null}
        onOpenChange={open => {
          if (!open) setSelectedEmployment(null);
        }}
      >
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-bold">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                <UserCheck className="h-4 w-4 text-primary" />
              </span>
              Employee Inquiry Details
            </DialogTitle>
          </DialogHeader>

          {selectedEmployment && (
            <div className="space-y-4 text-sm">
              <div className="rounded-xl border border-border bg-muted/30 px-4 py-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  Applicant
                </p>
                <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xl font-bold text-foreground">
                    {selectedEmployment.name}
                  </p>
                  <Badge
                    variant="outline"
                    className={cn(
                      "font-medium",
                      selectedEmployment.is_looking_for_employment
                        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                        : "border-muted bg-muted/40 text-muted-foreground"
                    )}
                  >
                    {selectedEmployment.is_looking_for_employment
                      ? "Looking for Employment"
                      : "General Inquiry"}
                  </Badge>
                </div>
                {selectedEmployment.created_at && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Submitted on{" "}
                    {new Date(selectedEmployment.created_at).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <InfoRow
                  icon={Phone}
                  label="Mobile Number"
                  value={selectedEmployment.mobile_number}
                />
                <InfoRow
                  icon={Briefcase}
                  label="Seeking Job?"
                  value={
                    selectedEmployment.is_looking_for_employment
                      ? "Yes (Looking for Employment)"
                      : "No (General Enquiry)"
                  }
                />
              </div>

              {selectedEmployment.additional_details && (
                <div className="rounded-xl border border-border bg-muted/20 p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-primary" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Additional Details
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap">
                    {selectedEmployment.additional_details}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
